import { useState, useEffect, useContext, useMemo } from "react";
import DeletePopUP from "../../CommonPopUp/DeletePopUp";
import ViewTaskPopUp from "./PopUp/ViewTaskPopUp";
import TesterQueuePopUp from "./PopUp/TesterQueuePopUp";
import FloatingAgentWidget from "./FloatingAgentWidget";
import { getMyProjects } from "../../../../hooks/useProjects";
import { getTesterTasks, getMyDueStatus } from "../../../../hooks/useTaskSheet";
import { formatDate } from "../../../../utils/formatDate";
import { Header } from "../../MainDashboard/Header/Header";
import { Sidebar } from "../../MainDashboard/Sidebar/Sidebar";
import { UserContext } from "../../../../context/UserContext";

import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
const APP_TOOLTIP_ID = 'employee-task-grid-tooltip';

const BeaconIndicator = ({ color = '#40A2D3', size = '14px', title = '', className = '' }) => {
  const style = {
    '--beacon-indicator-color': color,
    '--beacon-indicator-size': size,
  };
  return (
    <div
      className={`beacon-indicator-wrapper ${className}`}
      data-tooltip-id={APP_TOOLTIP_ID}
      data-tooltip-content={title}
      data-tooltip-place="bottom"
    >
      <div className="beacon-indicator" style={style}></div>
    </div>
  );
};

const Notification = ({ title, message }) => {
  console.log(`--- NOTIFICATION ---\nTitle: ${title}\nMessage: ${message}\n--------------------`);
};

const commonThStyle = {
  backgroundColor: 'transparent',
  color: 'white',
};

// short tooltip list: "Login Page, API Setup +2 more"
const listForTooltip = (items = []) => {
  if (items.length === 0) return '';
  const shown = items.slice(0, 3).join(', ');
  return items.length > 3 ? `${shown} +${items.length - 3} more` : shown;
};


// ✅ NEW — focus panel built from the SAME corrected data as the blinkers
// (completed tasks / completed projects are never counted)
const FocusSummaryPanel = ({ loading, focus }) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const summary = focus?.summary;
  const recommended = focus?.recommended;
  if (!loading && (!summary || summary.totalOpen === 0)) return null;

  return (
    <div className="agent-panel-root">
      <style>{`
        .agent-panel-root { --agent-grad: linear-gradient(120deg, #6366F1 0%, #06B6D4 100%); margin: 0 4px 14px 4px; }
        .agent-panel { position: relative; display: flex; align-items: center; gap: 16px; padding: 14px 18px; border-radius: 12px; background: linear-gradient(135deg, #EEF2FF 0%, #ECFEFF 100%); border: 1px solid #C7D2FE; overflow: hidden; }
        .agent-panel-avatar { flex-shrink: 0; width: 44px; height: 44px; border-radius: 50%; background: var(--agent-grad); color: #fff; font-size: 20px; display: flex; align-items: center; justify-content: center; position: relative; }
        .agent-panel-avatar .pulse-dot { position: absolute; bottom: 0; right: 0; width: 11px; height: 11px; border-radius: 50%; background: #22C55E; border: 2.5px solid #EEF2FF; }
        .agent-panel-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; background: var(--agent-grad); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .agent-panel-body { font-size: 14.5px; color: #1E293B; line-height: 1.4; margin-top: 2px; }
        .agent-panel-stats { display: flex; gap: 10px; flex-shrink: 0; }
        .agent-stat { text-align: center; padding: 6px 14px; border-radius: 8px; background: #ffffffaa; min-width: 64px; }
        .agent-stat-num { font-size: 18px; font-weight: 700; color: #1E293B; line-height: 1; }
        .agent-stat-label { font-size: 10px; color: #64748B; text-transform: uppercase; letter-spacing: 0.03em; }
        .agent-stat.is-overdue .agent-stat-num { color: #DC2626; }
        .agent-stat.is-today .agent-stat-num { color: #0284C7; }
        .agent-panel-close { border: none; background: transparent; color: #94A3B8; font-size: 16px; flex-shrink: 0; cursor: pointer; padding: 4px 8px; }
        .agent-panel-close:hover { color: #475569; }
        @media (max-width: 768px) { .agent-panel { flex-wrap: wrap; } .agent-panel-stats { width: 100%; } }
      `}</style>

      <div className="agent-panel">
        <div className="agent-panel-avatar">
          🤖
          <span className="pulse-dot"></span>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="agent-panel-eyebrow">Your work agent</div>
          {loading ? (
            <div className="agent-panel-body text-muted">Checking your work...</div>
          ) : recommended ? (
            <div className="agent-panel-body">
              Focus on <strong>{recommended.taskName}</strong> — {recommended.projectName}
              {recommended.customerName && <span className="text-muted"> ({recommended.customerName})</span>}
              {recommended.daysOverdue > 0 ? (
                <span style={{ color: '#DC2626', fontWeight: 600 }}>
                  {' '}({recommended.daysOverdue} day{recommended.daysOverdue !== 1 ? 's' : ''} overdue)
                </span>
              ) : recommended.daysOverdue === 0 ? (
                <span style={{ color: '#0284C7', fontWeight: 600 }}> (due today)</span>
              ) : (
                <span className="text-muted"> ({recommended.priority} priority)</span>
              )}
            </div>
          ) : (
            <div className="agent-panel-body">Nothing urgent right now — you're all caught up.</div>
          )}
        </div>

        {!loading && summary && (
          <div className="agent-panel-stats">
            <div className={`agent-stat ${summary.overdueCount > 0 ? 'is-overdue' : ''}`}>
              <div className="agent-stat-num">{summary.overdueCount}</div>
              <div className="agent-stat-label">Overdue</div>
            </div>
            <div className={`agent-stat ${summary.dueTodayCount > 0 ? 'is-today' : ''}`}>
              <div className="agent-stat-num">{summary.dueTodayCount}</div>
              <div className="agent-stat-label">Due Today</div>
            </div>
            <div className="agent-stat">
              <div className="agent-stat-num">{summary.totalOpen}</div>
              <div className="agent-stat-label">Open</div>
            </div>
          </div>
        )}

        <button type="button" className="agent-panel-close" onClick={() => setDismissed(true)} title="Dismiss">✕</button>
      </div>
    </div>
  );
};

export const EmployeeTaskGrid = () => {
  const { user } = useContext(UserContext);

  const [isopen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isopen);

  const [TaskPopUpShow, setTaskPopUpShow] = useState(false);
  const [deletePopUpShow, setdeletePopUpShow] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [projects, setProjects] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [notifiedTomorrowProjects, setNotifiedTomorrowProjects] = useState(new Set());

  // Tester's own testing queue
  const [testerQueueShow, setTesterQueueShow] = useState(false);
  const [testerQueueCount, setTesterQueueCount] = useState(0);

  // my tasks per project: overdue / due today / open count / total count
  const [dueStatus, setDueStatus] = useState({});
  // ✅ NEW — becomes true once my task status has been loaded
  const [dueLoaded, setDueLoaded] = useState(false);
  // ✅ NEW — summary + recommended task for the top panel (from corrected data)
  const [dueFocus, setDueFocus] = useState(null);

  useEffect(() => {
    const styleId = 'beacon-indicator-styles';
    if (document.getElementById(styleId)) return;
    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent = `
      .beacon-indicator-wrapper { display: inline-block; vertical-align: middle; }
      .beacon-indicator {
        position: relative;
        width: var(--beacon-indicator-size);
        height: var(--beacon-indicator-size);
        background-color: var(--beacon-indicator-color);
        margin: 0 auto;
        border: none;
        border-radius: 50%;
        padding: 0;
        outline: none;
        display: block;
      }
      .beacon-indicator::after {
        content: '';
        width: calc(var(--beacon-indicator-size) * 0.35);
        height: calc(var(--beacon-indicator-size) * 0.35);
        position: absolute;
        top: 50%; left: 50%;
        transform: translateX(-50%) translateY(-50%);
        border-width: calc(var(--beacon-indicator-size) * 0.12);
        border-style: solid;
        border-color: var(--beacon-indicator-color);
        border-radius: 50%;
        animation: beacon-indicator-animation 1.5s infinite linear;
        animation-fill-mode: forwards;
        box-sizing: border-box;
      }
      @keyframes beacon-indicator-animation {
        0%   { width: 0; height: 0; opacity: 1; }
        25%  { width: calc(var(--beacon-indicator-size) * 1);   height: calc(var(--beacon-indicator-size) * 1);   opacity: 0.7; }
        50%  { width: calc(var(--beacon-indicator-size) * 1.2); height: calc(var(--beacon-indicator-size) * 1.2); opacity: 0.5; }
        75%  { width: calc(var(--beacon-indicator-size) * 2);   height: calc(var(--beacon-indicator-size) * 2);   opacity: 0.3; }
        100% { width: calc(var(--beacon-indicator-size) * 2.5); height: calc(var(--beacon-indicator-size) * 2.5); opacity: 0; }
      }
    `;
    document.head.appendChild(styleElement);
  }, []);

  const handleViewTask = (id) => {
    setSelectedId(id);
    setTaskPopUpShow(!TaskPopUpShow);
  };

  const handelDeleteClosePopUpClick = (id = null) => {
    setSelectedId(id);
    setdeletePopUpShow(!deletePopUpShow);
  };

  // days between project end date and today (negative = overdue)
  const getProjectDiffDays = (dateString) => {
    const today = new Date();
    const end = new Date(dateString);
    if (isNaN(end.getTime())) return null;
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return Math.round((end.getTime() - today.getTime()) / (1000 * 3600 * 24));
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getMyProjects();
        setProjects(data?.projects || []);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // load my task due status; refresh after closing the Task List popup
  // (the employee may have just updated work)
  useEffect(() => {
    if (TaskPopUpShow) return;
    const loadDueStatus = async () => {
      const res = await getMyDueStatus();
      if (res?.success) {
        setDueStatus(res.projects || {});
        setDueFocus({ summary: res.summary, recommended: res.recommended });
      }
      setDueLoaded(true);
    };
    loadDueStatus();
  }, [TaskPopUpShow]);

  // Keep a live count of pending testing-queue items for the badge
  useEffect(() => {
    const fetchTesterCount = async () => {
      try {
        const data = await getTesterTasks();
        if (data?.success) {
          const pending = (data.task || []).filter(
            t => t.qaStatus === 'pending_test' || t.qaStatus === 'testing'
          ).length;
          setTesterQueueCount(pending);
        }
      } catch (e) {
        // silent — this is just a badge count, not critical
      }
    };
    fetchTesterCount();
  }, [testerQueueShow]);

  // ✅ NEW — true when ALL of my tasks in this project are completed
  const isAllMyTasksDone = (projectId) => {
    if (!dueLoaded) return false;
    const info = dueStatus[projectId];
    if (!info) return false;
    return (info.totalCount || 0) > 0 && (info.openCount || 0) === 0;
  };

  // ✅ UPDATED — status + customer search + hide projects where all my tasks are done
  const filteredProjects = useMemo(() => {
    let result = projects;

    if (statusFilter) {
      result = result.filter(
        (p) => p.projectStatus && p.projectStatus.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (search && search.trim() !== "") {
      const lowerSearch = search.trim().toLowerCase();
      result = result.filter(
        (p) => p.custId?.custName && p.custId.custName.toLowerCase().includes(lowerSearch)
      );
    }

    result = result.filter((p) => !isAllMyTasksDone(p._id));

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, statusFilter, search, dueStatus, dueLoaded]);

  // ✅ UPDATED — pagination is now calculated from the filtered list
  const pagination = useMemo(() => {
    const totalProjects = filteredProjects.length;
    const totalPages = Math.ceil(totalProjects / itemsPerPage);
    const page = Math.min(currentPage, Math.max(totalPages, 1));
    return {
      currentPage: page,
      totalPages,
      totalProjects,
      limit: itemsPerPage,
      hasNextPage: page < totalPages,
      hasPrevPage: page > 1,
    };
  }, [filteredProjects, currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  const handleChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearch(searchTerm);
      setCurrentPage(1);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearch("");
    setCurrentPage(1);
  };

  const indexOfLastItem = pagination.currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredProjects.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    if (currentData && currentData.length > 0) {
      const newNotificationsSent = new Set(notifiedTomorrowProjects);
      let updatedNotifiedSet = false;
      currentData.forEach(project => {
        const projectStatusLower = project.projectStatus ? project.projectStatus.toLowerCase() : "";
        if (projectStatusLower === 'upcoming' || projectStatusLower === 'inprocess') {
          const diffDays = getProjectDiffDays(project.endDate);
          if (diffDays === 1 && !notifiedTomorrowProjects.has(project._id)) {
            Notification({ title: "Project Due Tomorrow", message: `Project "${project.name}" is due tomorrow.` });
            newNotificationsSent.add(project._id);
            updatedNotifiedSet = true;
          }
        }
      });
      if (updatedNotifiedSet) setNotifiedTomorrowProjects(newNotificationsSent);
    }
  }, [currentData, notifiedTomorrowProjects]);

  // ✅ UPDATED — Work Status cell: orange = overdue, blue = due today.
  // The PROJECT-level blinker now shows ONLY if I still have at least one
  // open (not completed) task in that project. Completed tasks never blink.
  const renderWorkStatus = (project) => {
    const projectStatusLower = project.projectStatus ? project.projectStatus.toLowerCase() : "";
    // ✅ NEW — Completed project: never show any blinker
    if (projectStatusLower === 'completed') return null;

    const projectOpen = projectStatusLower === 'upcoming' || projectStatusLower === 'inprocess';
    const diffDays = getProjectDiffDays(project.endDate);
    const myTasks = dueStatus[project._id] || { overdue: 0, dueToday: 0, overdueTasks: [], dueTodayTasks: [], openCount: 0, totalCount: 0 };

    // I still have unfinished work in this project
    const hasMyOpenWork = (myTasks.openCount || 0) > 0;

    const overdueLines = [];
    const dueTodayLines = [];

    if (projectOpen && hasMyOpenWork && diffDays !== null && diffDays < 0) {
      const d = Math.abs(diffDays);
      overdueLines.push(`Project overdue by ${d} day${d === 1 ? '' : 's'}`);
    }
    if (projectOpen && hasMyOpenWork && diffDays === 0) {
      dueTodayLines.push('Project due today');
    }
    if (myTasks.overdue > 0) {
      overdueLines.push(`${myTasks.overdue} task${myTasks.overdue === 1 ? '' : 's'} overdue: ${listForTooltip(myTasks.overdueTasks)}`);
    }
    if (myTasks.dueToday > 0) {
      dueTodayLines.push(`${myTasks.dueToday} task${myTasks.dueToday === 1 ? '' : 's'} due today: ${listForTooltip(myTasks.dueTodayTasks)}`);
    }

    if (overdueLines.length === 0 && dueTodayLines.length === 0) return null;

    return (
      <span className="d-inline-flex align-items-center" style={{ gap: '16px' }}>
        {overdueLines.length > 0 && (
          <BeaconIndicator
            color="#F29339"
            size="14px"
            title={overdueLines.join(' | ')}
            className="indicate_width"
          />
        )}
        {dueTodayLines.length > 0 && (
          <BeaconIndicator
            color="#40A2D3"
            size="14px"
            title={dueTodayLines.join(' | ')}
            className="indicate_width"
          />
        )}
      </span>
    );
  };

  return (
    <>
      {(loading || !dueLoaded) && (
        <div className="overlay">
          <span className="loader"></span>
        </div>
      )}
      <div className="container-scroller">
        <div className="row background_main_all">
          <Header toggle={toggle} isopen={isopen} />
          <div className="container-fluid page-body-wrapper">
            <Sidebar isopen={isopen} active="EmployeeTaskGrid" />
            <div className="main-panel" style={{ width: isopen ? "" : "calc(100% - 120px)", marginLeft: isopen ? "" : "125px" }}>
              <div className="content-wrapper ps-3 ps-md-0 pt-3">

                {/* ── Title + Search + Filter + Testing Queue + Legend ── */}
                <div className="row px-2 py-1 align-items-center">
                  <div className="col-12 col-lg-2">
                    <h5 className="text-white py-2 mb-0">My Projects</h5>
                  </div>

                  <div className="col-12 col-lg-3">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control bg_edit"
                        placeholder="Search Customer..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                      />
                      {searchTerm && (
                        <button type="button" className="btn btn-light border-start-0" onClick={handleClearSearch}>
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-lg-2">
                    <select className="form-select bg_edit" name="projectStatus" onChange={(e) => handleChange(e.target.value)}>
                      <option value="">All Status</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="inprocess">Inprocess</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>

                  <div className="col-12 col-lg-2 mt-2 mt-lg-0">
                    <button
                      type="button"
                      className="btn btn-info btn-sm w-100 text-white position-relative"
                      onClick={() => setTesterQueueShow(true)}
                    >
                      <i className="fa-solid fa-vial me-1"></i> Testing Queue
                      {testerQueueCount > 0 && (
                        <span className="badge rounded-pill bg-danger position-absolute top-0 start-100 translate-middle">
                          {testerQueueCount}
                        </span>
                      )}
                    </button>
                  </div>

                  <div className="col-12 col-lg-3 d-flex align-items-center justify-content-lg-end mt-2 mt-lg-0">
                    <div
                      className="d-inline-flex align-items-center px-3 py-2 rounded"
                      style={{
                        background: 'rgba(255,255,255,0.10)',
                        border: '1px solid rgba(255,255,255,0.20)',
                        gap: '14px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <span className="text-white" style={{ fontSize: '12px', fontWeight: 600 }}>
                        Work Status :
                      </span>
                      <span className="d-flex align-items-center" style={{ gap: '7px' }}>
                        <BeaconIndicator color="#F29339" size="12px" title="Project or one of your tasks is overdue" />
                        <span className="text-white" style={{ fontSize: '12px' }}>Overdue</span>
                      </span>
                      <span className="d-flex align-items-center" style={{ gap: '7px' }}>
                        <BeaconIndicator color="#40A2D3" size="12px" title="Project or one of your tasks is due today" />
                        <span className="text-white" style={{ fontSize: '12px' }}>Due Today</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-2">
                  <FocusSummaryPanel loading={!dueLoaded} focus={dueFocus} />
                </div>

                {/* ── Table ── */}
                <div className="row bg-white p-2 m-1 border rounded">
                  <div className="col-12 py-2">
                    <div className="table-responsive">
                      <table className="table table-striped table-class" id="table-id">
                        <thead>
                          <tr className="th_border">
                            <th style={commonThStyle}>Sr. No</th>
                            <th className="align_left_td td_width">Customer Name</th>
                            <th className="align_left_td td_width">Project Name</th>
                            <th style={commonThStyle}>PO Number</th>
                            <th>Project Status</th>
                            <th style={commonThStyle}>Finish Date</th>
                            <th style={commonThStyle}>Work Status</th>
                            <th style={commonThStyle}>Tasks</th>
                          </tr>
                        </thead>
                        <tbody className="broder my-4">
                          {currentData && currentData.length > 0 ? (
                            currentData.map((project, index) => (
                              <tr className="border my-4" key={project._id}>
                                <td className="w-3">{index + 1 + (pagination.currentPage - 1) * itemsPerPage}</td>
                                <td className="align_left_td td_width wrap-text-of-col">{project.custId?.custName || "N/A"}</td>
                                <td className="align_left_td td_width wrap-text-of-col">{project.name}</td>
                                <td>{project.purchaseOrderNo || "N/A"}</td>
                                <td>{project.projectStatus}</td>
                                <td>{formatDate(project.endDate)}</td>
                                {/* project + my tasks: orange overdue / blue due today */}
                                <td>{renderWorkStatus(project)}</td>
                                <td>
                                  <i
                                    onClick={() => handleViewTask(project._id)}
                                    className="fa-solid fa-eye Task_View_icon cursor-pointer"
                                    data-tooltip-id={APP_TOOLTIP_ID}
                                    data-tooltip-content="View Tasks"
                                    data-tooltip-place="bottom"
                                  ></i>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center">No Projects Found Matching Criteria</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* ── Pagination ── */}
                {!loading && pagination.totalPages > 1 && (
                  <div className="pagination-container text-center my-3">
                    <button onClick={() => handlePageChange(1)} disabled={!pagination.hasPrevPage} className="btn btn-dark btn-sm me-1" style={{ borderRadius: "4px" }}>First</button>
                    <button disabled={!pagination.hasPrevPage} onClick={() => handlePageChange(pagination.currentPage - 1)} className="btn btn-dark btn-sm me-1" style={{ borderRadius: "4px" }}>Previous</button>

                    {(() => {
                      const pageNumbers = [];
                      const maxPagesToShow = 5;
                      if (pagination.totalPages <= maxPagesToShow) {
                        for (let i = 1; i <= pagination.totalPages; i++) pageNumbers.push(i);
                      } else {
                        let startPage, endPage;
                        if (pagination.currentPage <= Math.ceil(maxPagesToShow / 2)) {
                          startPage = 1; endPage = maxPagesToShow;
                        } else if (pagination.currentPage + Math.floor(maxPagesToShow / 2) >= pagination.totalPages) {
                          startPage = pagination.totalPages - maxPagesToShow + 1; endPage = pagination.totalPages;
                        } else {
                          startPage = pagination.currentPage - Math.floor(maxPagesToShow / 2);
                          endPage = pagination.currentPage + Math.floor(maxPagesToShow / 2);
                        }
                        startPage = Math.max(1, startPage); endPage = Math.min(pagination.totalPages, endPage);
                        if (startPage > 1) { pageNumbers.push(1); if (startPage > 2) pageNumbers.push('...'); }
                        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
                        if (endPage < pagination.totalPages) { if (endPage < pagination.totalPages - 1) pageNumbers.push('...'); pageNumbers.push(pagination.totalPages); }
                      }
                      return pageNumbers.map((number, idx) =>
                        typeof number === 'number' ? (
                          <button key={number} onClick={() => handlePageChange(number)} className={`btn btn-sm me-1 ${pagination.currentPage === number ? "btn-primary" : "btn-dark"}`} style={{ minWidth: "35px", borderRadius: "4px" }}>{number}</button>
                        ) : (
                          <span key={`e-${idx}`} className="btn btn-sm btn-disabled me-1" style={{ minWidth: "35px", borderRadius: "4px", border: "1px solid #6c757d", cursor: "default" }}>{number}</span>
                        )
                      );
                    })()}

                    <button disabled={!pagination.hasNextPage} onClick={() => handlePageChange(pagination.currentPage + 1)} className="btn btn-dark btn-sm me-1" style={{ borderRadius: "4px" }}>Next</button>
                    <button onClick={() => handlePageChange(pagination.totalPages)} disabled={!pagination.hasNextPage} className="btn btn-dark btn-sm" style={{ borderRadius: "4px" }}>Last</button>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>

      {deletePopUpShow && (
        <DeletePopUP
          message={"Are you sure! Do you want to Delete ?"}
          cancelBtnCallBack={handelDeleteClosePopUpClick}
          heading="Delete Confirmation"
        />
      )}
      {TaskPopUpShow && (
        <ViewTaskPopUp
          message="Task Details"
          selectedId={selectedId}
          handleViewTask={handleViewTask}
        />
      )}

      {testerQueueShow && (
        <TesterQueuePopUp onClose={() => setTesterQueueShow(false)} />
      )}

      <FloatingAgentWidget userName={user?.name} />

      <Tooltip id={APP_TOOLTIP_ID} />
    </>
  );
}